import React from 'react';
import { useTheme, Box } from '@mui/material';
import { useTranslation } from "react-i18next";

import ISONumber from 'components/formatting/ISONumber';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import AbbreviateNumber from 'components/formatting/AbbreviateNumber';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getSingleActiveWidgetRequestByName, getSingleWidgetResponseByName, WidgetResponseItem } from 'system/widget/Widget';

 // Get the amount of records of selected category and the title
 function getRecordsAmount(apiResponseInfo: WidgetResponseItem | undefined, i18n:any): { amount: number, title: string } {
  if (!apiResponseInfo) {
    return { amount: 0, title: ``};
  }

  const response = apiResponseInfo?.responseBody || {};

  const keys = Object.keys(response);

  if (keys.length === 1 && keys[0] === '_total') {
    return { amount: response['_total'], title: i18n.t("records") };
  }

  // If there are multiple keys, we need to find the one that is not '_total'
  if (keys.length > 1) {
    const category = keys.find(key => key !== '_total');
    if (category) {
      const label = response?.[category]?.labels?.[0] || "";

      // Check if the response has a structure
      const nestedValue = response?.[category]?.series?.[0]?.values?.[0];

      // If there is a value at the nested location, use it
      if (nestedValue !== undefined && nestedValue !== null) {
        const title = `${category} - ${label.charAt(0).toUpperCase() + label.slice(1)}`;
        return { amount: nestedValue, title: title };
      }

      // If the nested structure exists but has no value, return 0
      if (response?.[category]?.series) {
        return { amount: 0, title: category };
      }

      // If the response structure is different, use response[category] directly
      const fallbackValue = response[category];
      return { amount: fallbackValue, title: i18n.exists(category) ? i18n.t(category) : category };
    }
  }

  return { amount: 0, title: `` };
}

function NumericCardContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'numeric-card');
  const activeRequest = getSingleActiveWidgetRequestByName(props.activeWidgetRequests, 'numeric-card');
  let { amount, title } = getRecordsAmount(apiResponseInfo, i18n);

  if (amount && !props.widget?.data?.settings?.showDecimals) {
    amount = parseFloat(amount.toFixed(0));
  }

  const displayColor = props.widget?.data?.settings?.color || activeRequest?.queryRequestColor || theme.colors.primary.dark;

  return (
    <>
      {(amount === 0 || amount > 0) &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={props.widget?.data?.settings?.widgetTitleAlignment}
              settingsTitle={props.widget?.data?.settings?.widgetTitle}
              defaultTitle={`${title}`}
              customTitle={props.widget?.data?.settings?.customWidgetTitle}
              widgetQueryTitle={apiResponseInfo?.queryDisplayTitle} />
          }
        >
          <Box sx={{ width: "100%", height: "100%", p: 1 }}>
              <>
                {props.widget?.data?.settings?.abbreviate &&
                  <AbbreviateNumber
                    fitToContainer={true}
                    typographyVariant="h3"
                    options={{
                      maximumFractionDigits: props.widget.data.settings.showDecimals ? 2 : 0
                    }}
                    sx={{
                      color: displayColor,
                      height: "100%",
                      position: "relative",
                      textAlign: props.widget?.data?.settings?.alignment
                    }}
                    value={amount}
                  />
                }
                {!props.widget?.data?.settings?.abbreviate &&
                  <ISONumber
                    fitToContainer={true}
                    typographyVariant="h3"
                    includeDecimals={props.widget?.data?.settings?.showDecimals}
                    sx={{
                      alignItems: "center",
                      color: displayColor,
                      display: "flex",
                      height: "100%",
                      position: "relative",
                      justifyContent: props.widget?.data?.settings?.alignment ?? "left"
                    }}
                    number={amount}
                  />
                }
              </>
            </Box>
        </WidgetLayout>
      }
      {(amount === null || amount === undefined) &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(NumericCardContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
